/**
 * The frame of every widget — the v1.9 grammar (ROADMAP v1.9 D6), unchanged:
 *
 *   header      identity only — a title, a tab strip, or a breadcrumb — then the
 *               tile's own expand and close, behind a divider, always in the
 *               same place.
 *   action bar  scope on the left, an optional centre, commands on the right;
 *               at most one primary command, the rest in the ⋯ menu.
 *   body        the widget, which scrolls inside itself.
 *
 * The frame is `position: relative`, and so is its body: an overlay inside a
 * widget must anchor to the widget, not to the shell (v1.91).
 */
import type { ReactNode } from "react";

import { useTile } from "../../shell/tile";
import styles from "./WidgetFrame.module.css";
import { useTabScroll } from "./useTabScroll";

export interface Tab {
  id: string;
  label: string;
  badge?: number | string | null;
}

interface Props {
  title?: string;
  tabs?: readonly Tab[];
  activeTab?: string;
  onTab?: (id: string) => void;
  tabsLabel?: string;
  /** Identity that is neither a title nor tabs: a breadcrumb (Drive). */
  header?: ReactNode;
  /** A third element in the header, between identity and the tile's chrome. */
  nav?: ReactNode;
  scope?: ReactNode;
  center?: ReactNode;
  commands?: ReactNode;
  children?: ReactNode;
}

export function WidgetFrame({
  title,
  tabs,
  activeTab,
  onTab,
  tabsLabel = "Sections",
  header,
  nav,
  scope,
  center,
  commands,
  children,
}: Props) {
  const tile = useTile();
  const { ref: stripRef, edges } = useTabScroll<HTMLElement>();
  const fade = [edges.left && styles.fadeLeft, edges.right && styles.fadeRight]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.widget}>
      <div className={styles.head}>
        {tabs?.length ? (
          <nav
            ref={stripRef}
            className={`${styles.tabs} ${fade}`}
            role="tablist"
            aria-label={tabsLabel}
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={tab.id === activeTab ? `${styles.tab} ${styles.on}` : styles.tab}
                role="tab"
                aria-selected={tab.id === activeTab}
                onClick={() => tab.id !== activeTab && onTab?.(tab.id)}
              >
                {tab.label}
                {tab.badge ? <span className={styles.badge}>{tab.badge}</span> : null}
              </button>
            ))}
          </nav>
        ) : title ? (
          <span className={styles.title}>{title}</span>
        ) : (
          <div className={styles.ident}>{header}</div>
        )}
        {nav}
        <div className={styles.chrome}>
          {tile.isWide && (
            <button
              className={styles.chromeButton}
              onClick={tile.expanded ? tile.collapse : tile.expand}
              aria-label={tile.expanded ? "Collapse" : "Expand to full width"}
              title={tile.expanded ? "Collapse to side-by-side" : "Expand to full width"}
            >
              {tile.expanded ? <CollapseIcon /> : <ExpandIcon />}
            </button>
          )}
          <button className={styles.chromeButton} onClick={tile.close} aria-label="Close">
            ✕
          </button>
        </div>
      </div>

      {(scope || center || commands) && (
        <div className={styles.bar}>
          <div className={center ? `${styles.barLeft} ${styles.balanced}` : styles.barLeft}>
            {scope}
          </div>
          {center && <div className={styles.barCenter}>{center}</div>}
          <div className={center ? `${styles.barRight} ${styles.balanced}` : styles.barRight}>
            {commands}
          </div>
        </div>
      )}

      <div className={styles.body}>{children}</div>
    </div>
  );
}

function ExpandIcon() {
  return (
    <svg aria-hidden="true" width="13" height="13" viewBox="0 0 14 14" fill="none">
      <path
        d="M5 2H2v3M9 2h3v3M5 12H2V9M9 12h3V9"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CollapseIcon() {
  return (
    <svg aria-hidden="true" width="13" height="13" viewBox="0 0 14 14" fill="none">
      <path
        d="M2 5h3V2M9 5h3V2M2 9h3v3M9 9h3v3"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
